import React, { memo } from 'react';
import { Paper, Grid, Title, Text, Group, Button } from '@mantine/core';
import { IconSettings, IconRefresh, IconLogout } from '@tabler/icons-react';
import { KeycloakProfile } from 'keycloak-js';
import { useSettings } from 'hooks/useSettings';

/**
 * Props for the IntraHeader component.
 */
interface IntraHeaderProps {
    /** Currently authenticated user profile from Keycloak, null if not logged in */
    user: KeycloakProfile | null;
    /** Whether data is currently being loaded/refreshed */
    loading: boolean;
    /** Callback to trigger a data refresh */
    onRefresh: () => void;
    /** Callback to log out the current user */
    onLogout: () => void;
}

/**
 * Header component for the Intra admin dashboard.
 *
 * Displays:
 * - Application title with settings icon
 * - Subtitle describing the admin view purpose
 * - Currently logged-in user information
 * - Refresh data button with loading state
 * - Logout button
 *
 * Responsive layout with mobile-friendly button sizing.
 *
 * @param props - The component props
 * @returns A header section with user info and action buttons
 */
const IntraHeader: React.FC<IntraHeaderProps> = ({ user, loading, onRefresh, onLogout }) => {
    const { settings } = useSettings();
    const appTitle = settings?.appTitle || (settings?.eventName ? `${settings.eventName} Intra` : 'Tammilanit Intra');
    
    return (
        <Paper shadow="sm" p="md" radius="md" withBorder mb="md">
            <Grid gutter="md">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Title order={2} mb="xs">
                        <Group gap="xs">
                            <IconSettings size={24} />
                            <span>{appTitle}</span>
                        </Group>
                    </Title>
                    <Text size="sm" c="dimmed" mb="xs">Hallintanäkymä järjestäjille</Text>
                    {user && (
                        <Text size="sm">
                            Kirjautunut: <strong>{user.firstName || user.username}</strong> (
                            {user.email ?? user.username})
                        </Text>
                    )}
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Group gap="xs" wrap="wrap" justify="flex-end">
                        <Button 
                            color="violet.4"
                            leftSection={<IconRefresh size={16} />}
                            onClick={onRefresh} 
                            loading={loading}
                            className="mobile-button"
                            size="sm"
                        >
                            <span className="mobile-hidden">Päivitä tiedot</span>
                        </Button>
                        <Button
                            color="violet.1"
                            leftSection={<IconLogout size={16} />}
                            onClick={onLogout}
                            className="mobile-button"
                            size="sm"
                        >
                            <span className="mobile-hidden">Kirjaudu ulos</span>
                        </Button>
                    </Group>
                </Grid.Col>
            </Grid>
        </Paper>
    );
};

export default memo(IntraHeader);

